import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import AuditGrade from '@totem/components/controlSystemReport/AuditGrade';
import ControlSystemEditModal from '@totem/components/controlSystemReport/ControlSystemEditModal';
import ReportContext from '@totem/components/controlSystemReport/ControlSystemReportContext';
import { ControlSystemReportRecord } from '@totem/components/controlSystemReport/dataTypes';
import DeleteControlSystemModal from '@totem/components/controlSystemReport/DeleteControlSystemModal';
import DevicesContainer from '@totem/components/controlSystemReport/devices/devicesContainer';
import EventChart from '@totem/components/controlSystemReport/EventChart';
import EventLegend from '@totem/components/controlSystemReport/EventLegend';
import TableTitle from '@totem/components/controlSystemReport/TableTitle';
import TicketChart from '@totem/components/controlSystemReport/TicketChart';
import TicketLegend from '@totem/components/controlSystemReport/TicketLegend';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import { isNotNull } from '@totem/utilities/common';

import './controlSystemReport.css';

const styles = {
  button: {
    marginRight: '4px',
  },
};

const BuildingDetailControlSystemTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { reportData, loading, totalRecords } = useContext(ReportContext);
  const { input, handleTableChange, getFilterValues, getFilterOptions } =
    useContext(InputContext);
  const [selectedForDelete, setSelectedForDelete] =
    useState<ControlSystemReportRecord>(null);
  const [selectedForEdit, setSelectedForEdit] =
    useState<ControlSystemReportRecord>(null);
  const [action, setAction] = useState<string>('');

  const isDisabled =
    !permissions.organizationalAdministrator.hasPermissions(userProfile);

  const handleEditClicked = (record: ControlSystemReportRecord) => {
    setSelectedForEdit(record);

    setAction('edit');
  };

  const handleDeleteClicked = (record: ControlSystemReportRecord) => {
    setSelectedForDelete(record);

    setAction('delete');
  };

  const columns: ColumnProps<ControlSystemReportRecord>[] = [
    {
      title: <TableTitle>System</TableTitle>,
      dataIndex: 'name',
      key: 'name',
      render: (_, record: ControlSystemReportRecord) => (
        <Link to={`/dashboard/controlsystems/${record.id}`}>{record.name}</Link>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.name === compB.name ? 0 : compA.name > compB.name ? 1 : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('name'),
      filters: getFilterOptions('name'),
    },
    {
      title: <TableTitle>Manufacturer</TableTitle>,
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      render: (_, record: ControlSystemReportRecord) => (
        <span>{record.manufacturer}</span>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.manufacturer === compB.manufacturer
          ? 0
          : compA.manufacturer > compB.manufacturer
            ? 1
            : -1,
    },
    {
      title: <TableTitle>Audit Grade</TableTitle>,
      dataIndex: 'grade',
      key: 'grade',
      render: (_, record: ControlSystemReportRecord) => (
        <div styleName="gradeLink">
          {isNotNull(record.policyAudit) &&
            record.policyAudit.id !== '' &&
            record.policyAudit.source !== '' && (
              <Link
                to={`/dashboard/policyaudits/${record.policyAudit.id}/report`}
                target={record.policyAudit.id}
              >
                <AuditGrade
                  score={
                    (record.policyAudit.totalScore /
                      record.policyAudit.maxScore) *
                    100
                  }
                  size="small"
                  style={{ margin: '0 auto' }}
                />
              </Link>
            )}
        </div>
      ),
    },
    {
      title: (
        <TableTitle>
          <span title="Software End-of-Support">SW Lifecycle</span>
          <br />
          <span title="Past End-of-Support / Near End-of-Support">
            Past EOS / Near EOS
          </span>
        </TableTitle>
      ),
      dataIndex: 'devices',
      key: 'software',
      align: 'center',
      render: (_, record: ControlSystemReportRecord) => {
        const past =
          isNotNull(record.software) &&
          isNotNull(record.software.pastEndOfSupport)
            ? record.software.pastEndOfSupport
            : '-';
        const approaching =
          isNotNull(record.software) &&
          isNotNull(record.software.approachingEndOfSupport)
            ? record.software.approachingEndOfSupport
            : '-';
        return (
          <span>
            {past} / {approaching}
          </span>
        );
      },
    },
    {
      title: (
        <TableTitle>
          Events
          <br />
          <EventLegend />
        </TableTitle>
      ),
      dataIndex: 'eventChart',
      key: 'eventChart',
      render: (_, record: ControlSystemReportRecord) => {
        if (record.events !== null && record.events.length > 0) {
          return (
            <EventChart
              controlSystemId={record.id}
              eventSummary={record.events}
              eventRange={record.eventRange}
            />
          );
        }
        return <span style={{ color: colors.neutral.dim }}>No Events</span>;
      },
    },
    {
      title: (
        <TableTitle>
          Tickets
          <br />
          <TicketLegend />
        </TableTitle>
      ),
      dataIndex: 'ticketsChart',
      key: 'ticketsChart',
      render: (_, record: ControlSystemReportRecord) => {
        if (record.tickets !== null && record.tickets.length > 0) {
          return (
            <TicketChart
              controlSystemId={record.id}
              ticketSummary={record.tickets}
            />
          );
        }
        return <span style={{ color: colors.neutral.dim }}>No Tickets</span>;
      },
    },
    {
      title: <TableTitle>Actions</TableTitle>,
      dataIndex: '',
      width: '84px',
      render: (_, controlSystem) => (
        <div className="center-table-cell">
          <Tooltip title="Edit" placement="top">
            <Button
              shape="circle"
              icon={<EditOutlined />}
              disabled={isDisabled}
              style={styles.button}
              onClick={() => handleEditClicked(controlSystem)}
            />
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <Button
              shape="circle"
              icon={<DeleteOutlined color={colors.neutral.gray} />}
              disabled={isDisabled}
              onClick={() => handleDeleteClicked(controlSystem)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const getData = () => {
    if (
      typeof reportData !== 'undefined' &&
      reportData !== null &&
      reportData.report !== null
    ) {
      return reportData.report;
    }
    return [];
  };

  const handleCloseDialog = () => {
    setAction('');

    setSelectedForDelete(null);

    setSelectedForEdit(null);
  };

  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={getData()}
        onChange={(
          updatedPagination: TablePaginationConfig,
          filters,
          sorter: SorterResult<ControlSystemReportRecord>,
          extra,
        ) => handleTableChange(updatedPagination, filters, sorter, extra)}
        loading={loading}
        rowKey={(record) => record.id}
        pagination={{
          current: input.pagination.page,
          pageSize: input.pagination.pageSize,
          total: totalRecords,
          showSizeChanger: true,
        }}
        expandable={{
          rowExpandable: (record) => record.devices.length > 0,
          expandedRowRender: (record) => (
            <DevicesContainer
              controlSystemId={record.id}
              eventRange={record.eventRange}
            />
          ),
          defaultExpandAllRows: false,
        }}
      />
      {action === 'edit' && (
        <ControlSystemEditModal
          open={action === 'edit' && isNotNull(selectedForEdit)}
          onClose={handleCloseDialog}
          controlSystem={selectedForEdit}
        />
      )}
      {action === 'delete' && (
        <DeleteControlSystemModal
          open={action === 'delete' && isNotNull(selectedForDelete)}
          onClose={handleCloseDialog}
          controlSystem={selectedForDelete}
        />
      )}
    </>
  );
};

export default BuildingDetailControlSystemTable;
